import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import {
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { toast } from 'react-toastify';

import { libraryStates } from '../../utils/library-states';
import {
	API_BASE,
	COLUMN_SIZING_STORAGE_KEY,
	STICKY_COLUMNS_STORAGE_KEY,
} from './constants';
import {
	ChangeModal,
	LogsModal,
	MultiSelect,
	PaymentsModal,
	Select,
} from './components';
import { buildInstitutionsColumns } from './columns';
import {
	classNames,
	exportCsv,
	fetchInstitutionLogs,
	fetchInstitutionPayments,
	fetchSalesRepresentatives,
	formatDateForBackend,
	getStickyLeft,
	getStoredColumnSizing,
	getStoredStickyColumns,
	getStoredUserEmail,
	patchInstitutionField,
	useDebouncedValue,
} from './utils';

function getInitialChangeModalState() {
	return {
		open: false,
		rowId: null,
		field: '',
		oldValue: '',
		newValue: '',
		description: '',
		email: localStorage?.getItem('email'),
	};
}

function buildOptimisticRowPatch(field, value) {
	if (field !== 'state_id') {
		return { [field]: value };
	}

	const matchedState = libraryStates.find(
		(state) => String(state.value) === String(value),
	);

	return {
		state_id: value,
		state: matchedState
			? {
					id: Number(value),
					name: matchedState.label,
				}
			: null,
	};
}

function applyOptimisticCellUpdate(rows, rowId, field, value) {
	return rows.map((item) =>
		item.id === rowId ? { ...item, ...buildOptimisticRowPatch(field, value) } : item,
	);
}

function buildPatchPayload(field, formattedValue, description, email) {
	if (field === 'state_id') {
		return {
			state_id: formattedValue ? Number(formattedValue) : null,
			description,
			email,
		};
	}

	return {
		[field]: formattedValue,
		description,
		email,
	};
}

export default function InstitutionsTable() {
	const userEmail = getStoredUserEmail();
	const authToken = localStorage.getItem('token');

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [savingCell, setSavingCell] = useState(null);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebouncedValue(search, 350);
	const [stateIds, setStateIds] = useState([]);
	const [ebooks, setEbooks] = useState(null);
	const [category, setCategory] = useState(null);
	const [language, setLanguage] = useState('en');
	const [sorting, setSorting] = useState([{ id: 'id', desc: true }]);
	const [salesRepresentative, setSalesRepresentative] = useState(null);
	const [data, setData] = useState([]);
	const [count, setCount] = useState(0);
	const [pages, setPages] = useState(0);
	const [changeModal, setChangeModal] = useState(getInitialChangeModalState);
	const [stickyColumns, setStickyColumns] = useState(getStoredStickyColumns);
	const [logsModalOpen, setLogsModalOpen] = useState(false);
	const [logsInstitution, setLogsInstitution] = useState(null);
	const [logsLoading, setLogsLoading] = useState(false);
	const [logsError, setLogsError] = useState('');
	const [logsData, setLogsData] = useState([]);
	const [logsPage, setLogsPage] = useState(1);
	const [logsPages, setLogsPages] = useState(0);
	const [logsCount, setLogsCount] = useState(0);
	const [salesRepOptions, setSalesRepOptions] = useState([]);
	const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
	const [paymentsInstitution, setPaymentsInstitution] = useState(null);
	const [paymentsLoading, setPaymentsLoading] = useState(false);
	const [paymentsError, setPaymentsError] = useState('');
	const [paymentsData, setPaymentsData] = useState([]);
	const [paymentsPage, setPaymentsPage] = useState(1);
	const [paymentsPages, setPaymentsPages] = useState(0);
	const [paymentsCount, setPaymentsCount] = useState(0);
	const [columnSizing, setColumnSizing] = useState(getStoredColumnSizing);
	const [contractExpiringInMonths, setContractExpiringInMonths] =
		useState(null);
	const [contractExpiryMode, setContractExpiryMode] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [rowSelection, setRowSelection] = useState({});

	const abortRef = useRef(null);

	const sortBy = sorting?.[0]?.id || 'code';
	const sortDir = sorting?.[0]?.desc ? 'desc' : 'asc';

	const closeChangeModal = useCallback(() => {
		setChangeModal(getInitialChangeModalState());
	}, []);

	const closeLogsModal = useCallback(() => {
		setLogsModalOpen(false);
		setLogsInstitution(null);
		setLogsData([]);
		setLogsError('');
	}, []);

	const closePaymentsModal = useCallback(() => {
		setPaymentsModalOpen(false);
		setPaymentsInstitution(null);
		setPaymentsData([]);
		setPaymentsError('');
	}, []);

	const loadPaymentsPage = useCallback(
		async (nextPage) => {
			if (!paymentsInstitution) return;

			setPaymentsLoading(true);
			setPaymentsError('');

			try {
				const json = await fetchInstitutionPayments({
					institutionId: paymentsInstitution.id,
					page: nextPage,
					pageSize: 25,
					sortBy: 'date_payment',
					sortDir: 'desc',
					token: authToken,
				});

				setPaymentsData(json.data || []);
				setPaymentsCount(json.count || 0);
				setPaymentsPages(json.pages || 0);
				setPaymentsPage(nextPage);
			} catch (requestError) {
				setPaymentsError(requestError?.message || 'Failed to load payments');
			} finally {
				setPaymentsLoading(false);
			}
		},
		[authToken, paymentsInstitution],
	);

	const openPaymentsModal = useCallback(
		async (institution) => {
			setPaymentsModalOpen(true);
			setPaymentsInstitution(institution);
			setPaymentsLoading(true);
			setPaymentsError('');
			setPaymentsData([]);
			setPaymentsPage(1);
			setPaymentsPages(0);
			setPaymentsCount(0);

			try {
				const json = await fetchInstitutionPayments({
					institutionId: institution.id,
					page: 1,
					pageSize: 25,
					sortBy: 'date_payment',
					sortDir: 'desc',
					token: authToken,
				});

				setPaymentsData(json.data || []);
				setPaymentsCount(json.count || 0);
				setPaymentsPages(json.pages || 0);
			} catch (requestError) {
				setPaymentsError(requestError?.message || 'Failed to load payments');
			} finally {
				setPaymentsLoading(false);
			}
		},
		[authToken],
	);

	const loadLogsPage = useCallback(
		async (nextPage) => {
			if (!logsInstitution) return;

			setLogsLoading(true);
			setLogsError('');

			try {
				const json = await fetchInstitutionLogs({
					instituteId: logsInstitution.id,
					page: nextPage,
					pageSize: 25,
					sortDir: 'desc',
					token: authToken,
				});

				setLogsData(json.data || []);
				setLogsCount(json.count || 0);
				setLogsPages(json.pages || 0);
				setLogsPage(nextPage);
			} catch (requestError) {
				setLogsError(requestError?.message || 'Failed to load logs');
			} finally {
				setLogsLoading(false);
			}
		},
		[authToken, logsInstitution],
	);

	const openLogsModal = useCallback(
		async (institution) => {
			setLogsModalOpen(true);
			setLogsInstitution(institution);
			setLogsLoading(true);
			setLogsError('');
			setLogsData([]);
			setLogsPage(1);
			setLogsPages(0);
			setLogsCount(0);

			try {
				const json = await fetchInstitutionLogs({
					instituteId: institution.id,
					page: 1,
					pageSize: 25,
					sortDir: 'desc',
					token: authToken,
				});

				setLogsData(json.data || []);
				setLogsCount(json.count || 0);
				setLogsPages(json.pages || 0);
			} catch (requestError) {
				setLogsError(requestError?.message || 'Failed to load logs');
			} finally {
				setLogsLoading(false);
			}
		},
		[authToken],
	);

	const saveField = useCallback(
		async (rowId, field, value, options = {}) => {
			const row = data.find((item) => item.id === rowId);
			if (!row) return;

			const oldValue = row[field] ?? null;
			const formattedValue =
				field === 'Date_demo' ? formatDateForBackend(value) : value;

			if (String(oldValue ?? '') === String(value ?? '')) return;

			if (options.requireModal) {
				setChangeModal({
					open: true,
					rowId,
					field,
					oldValue,
					newValue: value,
					description: '',
					email: localStorage.getItem('email'),
				});
				return;
			}

			const cellKey = `${rowId}:${field}`;
			setSavingCell(cellKey);
			setData((previous) =>
				applyOptimisticCellUpdate(previous, rowId, field, formattedValue),
			);

			try {
				await patchInstitutionField({
					id: rowId,
					payload: buildPatchPayload(
						field,
						formattedValue,
						'',
						userEmail?.trim() || null,
					),
					token: authToken,
				});

				toast.success('Institution updated successfully');
			} catch (requestError) {
				setData((previous) =>
					applyOptimisticCellUpdate(previous, rowId, field, oldValue),
				);
				toast.error(requestError?.message || 'Failed to update institution');
			} finally {
				setSavingCell(null);
			}
		},
		[authToken, data, userEmail],
	);

	const confirmModalChange = useCallback(async () => {
		const { rowId, field, oldValue, newValue, description, email } = changeModal;
		const formattedValue =
			field === 'Date_demo' ? formatDateForBackend(newValue) : newValue;

		if (!rowId || !field) return;

		if (!description.trim()) {
			toast.error('Description is required');
			return;
		}

		if (!email.trim()) {
			toast.error('Email is required');
			return;
		}

		const cellKey = `${rowId}:${field}`;
		setSavingCell(cellKey);
		setData((previous) =>
			applyOptimisticCellUpdate(previous, rowId, field, formattedValue),
		);

		try {
			await patchInstitutionField({
				id: rowId,
				payload: buildPatchPayload(
					field,
					formattedValue,
					description.trim(),
					email.trim(),
				),
				token: authToken,
			});

			toast.success('Institution updated successfully');
			closeChangeModal();
		} catch (requestError) {
			setData((previous) =>
				applyOptimisticCellUpdate(previous, rowId, field, oldValue),
			);
			toast.error(requestError?.message || 'Failed to update institution');
		} finally {
			setSavingCell(null);
		}
	}, [authToken, changeModal, closeChangeModal]);

	const fetchInstitutions = useCallback(async () => {
		setLoading(true);
		setError('');

		try {
			if (abortRef.current) abortRef.current.abort();

			const controller = new AbortController();
			abortRef.current = controller;

			const params = new URLSearchParams();
			params.set('page', String(page));
			params.set('page_size', String(pageSize));
			params.set('sort_by', sortBy);
			params.set('sort_dir', sortDir);

			if (debouncedSearch?.trim()) params.set('q', debouncedSearch.trim());

			if (Array.isArray(stateIds) && stateIds.length > 0) {
				stateIds.forEach((stateId) => {
					params.append('state_ids', String(stateId));
				});
			}

			if (ebooks != null) params.set('ebooks', String(ebooks === 'true'));
			if (category?.trim()) params.set('category', category.trim());
			if (language?.trim()) params.set('language', language.trim());
			if (salesRepresentative?.trim()) {
				params.set('sales_representative', salesRepresentative.trim());
			}

			if (contractExpiringInMonths != null) {
				params.set(
					'contract_expiring_in_months',
					String(contractExpiringInMonths),
				);
			}

			if (contractExpiryMode === 'current_month') {
				const now = new Date();
				params.set('contract_end_month', String(now.getMonth() + 1));
				params.set('contract_end_year', String(now.getFullYear()));
			}

			const response = await fetch(
				`${API_BASE}/institutions/basic?${params.toString()}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					signal: controller.signal,
				},
			);

			if (!response.ok) {
				const payload = await response.json().catch(() => ({}));
				throw new Error(payload?.detail || `Request failed (${response.status})`);
			}

			const json = await response.json();
			setData(json.data || []);
			setCount(json.count || 0);
			setPages(json.pages || 0);
		} catch (requestError) {
			if (requestError?.name !== 'AbortError') {
				setError(requestError?.message || 'Failed to load institutions');
			}
		} finally {
			setLoading(false);
		}
	}, [
		category,
		contractExpiringInMonths,
		contractExpiryMode,
		debouncedSearch,
		ebooks,
		language,
		page,
		pageSize,
		salesRepresentative,
		sortBy,
		sortDir,
		stateIds,
	]);

	const clearFilters = useCallback(() => {
		setSearch('');
		setStateIds([]);
		setEbooks(null);
		setCategory(null);
		setLanguage(null);
		setContractExpiringInMonths(null);
		setContractExpiryMode(null);
		setSalesRepresentative(null);
	}, []);

	useEffect(() => {
		setPage(1);
	}, [
		debouncedSearch,
		stateIds,
		ebooks,
		category,
		language,
		contractExpiringInMonths,
		salesRepresentative,
		contractExpiryMode,
		pageSize,
		sortBy,
		sortDir,
	]);

	useEffect(() => {
		let mounted = true;

		async function loadSalesReps() {
			try {
				const json = await fetchSalesRepresentatives({ token: authToken });
				if (!mounted) return;

				setSalesRepOptions(
					(json.data || []).map((item) => ({
						value: item.id,
						label: item.name,
					})),
				);
			} catch (requestError) {
				console.error(requestError);
				if (!mounted) return;
				setSalesRepOptions([]);
			}
		}

		loadSalesReps();

		return () => {
			mounted = false;
		};
	}, [authToken]);

	useEffect(() => {
		fetchInstitutions();
	}, [fetchInstitutions]);

	useEffect(() => {
		try {
			localStorage.setItem(
				COLUMN_SIZING_STORAGE_KEY,
				JSON.stringify(columnSizing || {}),
			);
		} catch (storageError) {
			console.error('Failed to persist column sizing', storageError);
		}
	}, [columnSizing]);

	useEffect(() => {
		try {
			localStorage.setItem(
				STICKY_COLUMNS_STORAGE_KEY,
				JSON.stringify(stickyColumns || {}),
			);
		} catch (storageError) {
			console.error('Failed to persist sticky columns', storageError);
		}
	}, [stickyColumns]);

	useEffect(() => {
		const shouldLockScroll =
			changeModal.open || logsModalOpen || paymentsModalOpen;

		if (!shouldLockScroll) return undefined;

		const originalBodyOverflow = document.body.style.overflow;
		const originalHtmlOverflow = document.documentElement.style.overflow;

		document.body.style.overflow = 'hidden';
		document.documentElement.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = originalBodyOverflow;
			document.documentElement.style.overflow = originalHtmlOverflow;
		};
	}, [changeModal.open, logsModalOpen, paymentsModalOpen]);

	useEffect(
		() => () => {
			if (abortRef.current) abortRef.current.abort();
		},
		[],
	);

	const stateOptions = useMemo(() => libraryStates, []);
	const stateFilterOptions = useMemo(
		() =>
			stateOptions.map((option) => ({
				value: String(option.value),
				label: option.label,
			})),
		[stateOptions],
	);
	const salesRepFilterOptions = useMemo(
		() =>
			salesRepOptions.map((option) => ({
				value: String(option.value),
				label: option.label,
			})),
		[salesRepOptions],
	);
	const selectedCount = useMemo(
		() => Object.keys(rowSelection || {}).length,
		[rowSelection],
	);

	const columns = useMemo(
		() =>
			buildInstitutionsColumns({
				savingCell,
				salesRepOptions,
				onSaveField: saveField,
				onOpenPaymentsModal: openPaymentsModal,
				onOpenLogsModal: openLogsModal,
			}),
		[openLogsModal, openPaymentsModal, salesRepOptions, saveField, savingCell],
	);

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
			rowSelection,
			columnSizing,
		},
		onSortingChange: setSorting,
		onRowSelectionChange: setRowSelection,
		onColumnSizingChange: setColumnSizing,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		enableRowSelection: true,
		enableColumnResizing: true,
		manualSorting: true,
	});

	return (
		<div className='h-full w-full min-h-0 min-w-0 bg-white'>
			<LogsModal
				open={logsModalOpen}
				institution={logsInstitution}
				loading={logsLoading}
				error={logsError}
				data={logsData}
				count={logsCount}
				page={logsPage}
				pages={logsPages}
				salesRepOptions={salesRepOptions}
				onClose={closeLogsModal}
				onPrev={() => loadLogsPage(Math.max(1, logsPage - 1))}
				onNext={() =>
					loadLogsPage(logsPages ? Math.min(logsPages, logsPage + 1) : logsPage + 1)
				}
			/>

			<PaymentsModal
				open={paymentsModalOpen}
				institution={paymentsInstitution}
				loading={paymentsLoading}
				error={paymentsError}
				data={paymentsData}
				count={paymentsCount}
				page={paymentsPage}
				pages={paymentsPages}
				onClose={closePaymentsModal}
				onPrev={() => loadPaymentsPage(Math.max(1, paymentsPage - 1))}
				onNext={() =>
					loadPaymentsPage(
						paymentsPages
							? Math.min(paymentsPages, paymentsPage + 1)
							: paymentsPage + 1,
					)
				}
			/>

			<ChangeModal
				open={changeModal.open}
				changeModal={changeModal}
				salesRepOptions={salesRepOptions}
				savingCell={savingCell}
				onClose={closeChangeModal}
				onConfirm={confirmModalChange}
				onDescriptionChange={(event) =>
					setChangeModal((previous) => ({
						...previous,
						description: event.target.value,
					}))
				}
				onEmailChange={(event) =>
					setChangeModal((previous) => ({
						...previous,
						email: event.target.value,
					}))
				}
			/>

			<div className='flex h-full min-h-0 w-full flex-col border border-zinc-200 bg-white'>
				<div className='flex shrink-0 flex-col gap-2 border-b border-zinc-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between'>
					<div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2'>
						<div className='flex items-center gap-2 text-xs font-medium text-zinc-700'>
							<svg
								width='16'
								height='16'
								viewBox='0 0 20 20'
								fill='currentColor'
								className='text-zinc-500'
							>
								<path
									fillRule='evenodd'
									d='M2 5a2 2 0 012-2h12a2 2 0 012 2v1a2 2 0 01-.586 1.414L12 13.828V17a1 1 0 01-1.447.894l-2-1A1 1 0 018 16v-2.172L2.586 7.414A2 2 0 012 6V5z'
									clipRule='evenodd'
								/>
							</svg>
							Filters
						</div>

						<div className='min-w-[260px]'>
							<MultiSelect
								value={stateIds}
								onChange={setStateIds}
								placeholder='States: All'
								options={stateFilterOptions}
								label='state'
							/>
						</div>

						<div className='min-w-[220px]'>
							<Select
								value={
									contractExpiryMode === 'current_month'
										? 'current_month'
										: contractExpiringInMonths != null
											? String(contractExpiringInMonths)
											: ''
								}
								onChange={(value) => {
									if (value === 'current_month') {
										setContractExpiryMode('current_month');
										setContractExpiringInMonths(null);
										return;
									}

									setContractExpiryMode(null);
									setContractExpiringInMonths(value ? Number(value) : null);
								}}
								placeholder='Contract expiry'
								options={[
									{ value: 'current_month', label: 'Expiring this month' },
									{ value: 1, label: 'Expiring in 1 month' },
									{ value: 2, label: 'Expiring in 2 months' },
									{ value: 4, label: 'Expiring in 4 months' },
									{ value: 6, label: 'Expiring in 6 months' },
									{ value: 12, label: 'Expiring in 12 months' },
								]}
							/>
						</div>

						<div className='min-w-[160px]'>
							<Select
								value={language}
								onChange={setLanguage}
								placeholder='Language: All'
								options={[
									{ value: 'en', label: 'English' },
									{ value: 'es', label: 'Spanish' },
								]}
							/>
						</div>

						<div className='min-w-[180px]'>
							<Select
								value={salesRepresentative}
								onChange={setSalesRepresentative}
								placeholder='Sales Rep: All'
								options={salesRepFilterOptions}
							/>
						</div>
					</div>

					<div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2'>
						<div className='relative min-w-[240px]'>
							<svg
								className='absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400'
								width='16'
								height='16'
								viewBox='0 0 20 20'
								fill='currentColor'
							>
								<path
									fillRule='evenodd'
									d='M12.9 14.32a8 8 0 111.414-1.414l3.387 3.387a1 1 0 01-1.414 1.414l-3.387-3.387zM14 8a6 6 0 11-12 0 6 6 0 0112 0z'
									clipRule='evenodd'
								/>
							</svg>
							<input
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder='Search institutions...'
								className='h-9 w-full rounded-lg border border-zinc-200 bg-white pl-9 pr-3 text-xs text-zinc-800 shadow-sm outline-none placeholder:text-zinc-400 focus:border-sky-300 focus:ring-4 focus:ring-sky-100'
							/>
						</div>

						<button
							type='button'
							onClick={clearFilters}
							className='text-xs font-medium text-sky-600 hover:text-sky-700'
						>
							Clear Filters
						</button>
					</div>
				</div>

				<div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
					<div className='min-h-0 flex-1 overflow-auto'>
						<table
							className='border-collapse table-fixed'
							style={{
								width: table.getCenterTotalSize(),
								minWidth: '100%',
								tableLayout: 'fixed',
							}}
						>
							<thead className='sticky top-0 z-20 bg-white'>
								{table.getHeaderGroups().map((headerGroup) => (
									<tr key={headerGroup.id} className='border-b border-zinc-200'>
										{headerGroup.headers.map((header) => {
											const canSort = header.column.getCanSort();
											const isSorted = header.column.getIsSorted();
											const columnId = header.column.id;
											const isSticky = !!stickyColumns?.[columnId];
											const showStickyShadow = isSticky && columnId === 'name';
											const stickyLeft = getStickyLeft(
												table,
												columnId,
												stickyColumns,
											);

											return (
												<th
													key={header.id}
													className={classNames(
														'relative select-none border-r border-zinc-200 bg-zinc-50 px-2 py-2 text-left text-[11px] font-semibold tracking-wide text-zinc-600',
														header.column.id === 'actions' ? 'text-right' : '',
														isSticky && 'sticky',
													)}
													style={{
														width: header.getSize(),
														left: isSticky ? `${stickyLeft}px` : undefined,
														zIndex: isSticky ? 30 : undefined,
														boxShadow: showStickyShadow
															? '2px 0 0 #d4d4d8, 12px 0 16px -8px rgba(0,0,0,0.18)'
															: undefined,
													}}
												>
													<div className='flex items-center justify-between gap-2'>
														<button
															type='button'
															className={classNames(
																'inline-flex min-w-0 items-center gap-1 truncate',
																canSort
																	? 'cursor-pointer select-none hover:text-zinc-800'
																	: 'cursor-default',
															)}
															onClick={
																canSort
																	? header.column.getToggleSortingHandler()
																	: undefined
															}
														>
															{flexRender(
																header.column.columnDef.header,
																header.getContext(),
															)}

															{canSort && (
																<span className='inline-flex h-4 w-4 items-center justify-center'>
																	{isSorted === 'asc' ? (
																		<svg
																			width='14'
																			height='14'
																			viewBox='0 0 20 20'
																			fill='currentColor'
																		>
																			<path d='M10 4l5 6H5l5-6z' />
																		</svg>
																	) : isSorted === 'desc' ? (
																		<svg
																			width='14'
																			height='14'
																			viewBox='0 0 20 20'
																			fill='currentColor'
																		>
																			<path d='M10 16l-5-6h10l-5 6z' />
																		</svg>
																	) : (
																		<svg
																			width='14'
																			height='14'
																			viewBox='0 0 20 20'
																			fill='currentColor'
																			className='opacity-30'
																		>
																			<path d='M10 4l5 6H5l5-6z' />
																			<path d='M10 16l-5-6h10l-5 6z' />
																		</svg>
																	)}
																</span>
															)}
														</button>
													</div>

													{header.column.getCanResize() && (
														<div
															onMouseDown={header.getResizeHandler()}
															onTouchStart={header.getResizeHandler()}
															className={classNames(
																'absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none bg-transparent hover:bg-sky-400/40',
																header.column.getIsResizing()
																	? 'bg-sky-500/60'
																	: '',
															)}
														/>
													)}
												</th>
											);
										})}
									</tr>
								))}
							</thead>

							<tbody className='bg-white'>
								{loading ? (
									<tr>
										<td colSpan={columns.length} className='px-4 py-10'>
											<div className='flex items-center justify-center gap-2 text-xs text-zinc-500'>
												<span className='h-4 w-4 animate-spin rounded-full border-2 border-zinc-300 border-t-transparent' />
												Loading institutions...
											</div>
										</td>
									</tr>
								) : error ? (
									<tr>
										<td colSpan={columns.length} className='px-4 py-10'>
											<div className='text-center text-xs text-rose-600'>
												{error}
											</div>
										</td>
									</tr>
								) : table.getRowModel().rows.length === 0 ? (
									<tr>
										<td colSpan={columns.length} className='px-4 py-10'>
											<div className='text-center text-xs text-zinc-500'>
												No results.
											</div>
										</td>
									</tr>
								) : (
									table.getRowModel().rows.map((row) => (
										<tr key={row.id} className='hover:bg-zinc-50'>
											{row.getVisibleCells().map((cell) => {
												const columnId = cell.column.id;
												const isSticky = !!stickyColumns?.[columnId];
												const showStickyShadow =
													isSticky && columnId === 'name';
												const stickyLeft = getStickyLeft(
													table,
													columnId,
													stickyColumns,
												);

												return (
													<td
														key={cell.id}
														className={classNames(
															'border-r border-b border-zinc-100 px-2 py-2 align-middle bg-white',
															cell.column.id === 'actions' ? 'text-right' : '',
															isSticky && 'sticky',
														)}
														style={{
															width: cell.column.getSize(),
															left: isSticky ? `${stickyLeft}px` : undefined,
															zIndex: isSticky ? 10 : undefined,
															boxShadow: showStickyShadow
																? '2px 0 0 #e4e4e7, 12px 0 16px -8px rgba(0,0,0,0.14)'
																: undefined,
															background: showStickyShadow
																? '#e6e6e6'
																: '#fffff',
														}}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext(),
														)}
													</td>
												);
											})}
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<div className='shrink-0 border-t border-zinc-200 bg-white px-3 py-2'>
						<div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
							<div className='flex flex-wrap items-center gap-3 text-xs'>
								{selectedCount > 0 ? (
									<>
										<span className='text-sky-600'>{selectedCount} selected</span>
										<span className='text-zinc-300'>|</span>
										<button
											type='button'
											className='inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100'
											onClick={() => alert('Wire Change Status action')}
										>
											<svg
												width='16'
												height='16'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='text-zinc-500'
											>
												<path d='M4 13V7h2v6H4zm10 0V7h2v6h-2zM8 15V5h4v10H8z' />
											</svg>
											Change Status
										</button>
										<button
											type='button'
											className='inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100'
											onClick={() => alert('Wire Activate/Deactivate action')}
										>
											<svg
												width='16'
												height='16'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='text-zinc-500'
											>
												<path d='M10 2a1 1 0 011 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4.007 4.007a1 1 0 01-1.414 0L5.272 9.707a1 1 0 011.414-1.414L9 10.586V3a1 1 0 011-1z' />
												<path d='M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z' />
											</svg>
											Activate/Deactivate
										</button>
										<button
											type='button'
											className='inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100'
											onClick={() => exportCsv(data)}
										>
											<svg
												width='16'
												height='16'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='text-zinc-500'
											>
												<path d='M3 3a1 1 0 011-1h6a1 1 0 01.707.293l6 6A1 1 0 0117 9v8a1 1 0 01-1 1H4a1 1 0 01-1-1V3z' />
												<path d='M9 2v6a1 1 0 001 1h6' />
											</svg>
											Export CSV
										</button>
									</>
								) : (
									<div>
										<span className='text-zinc-500'>
											Showing{' '}
											<span className='font-medium text-zinc-800'>
												{data.length ? (page - 1) * pageSize + 1 : 0}–
												{(page - 1) * pageSize + data.length}
											</span>{' '}
											of <span className='font-medium text-zinc-800'>{count}</span>
										</span>
										<button
											type='button'
											onClick={() =>
												setStickyColumns((previous) => ({
													...previous,
													name: !previous?.name,
												}))
											}
											className='rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50'
										>
											{stickyColumns?.name ? 'Unstick Name' : 'Stick Name'}
										</button>
									</div>
								)}
							</div>

							<div className='flex items-center gap-2'>
								<div className='hidden sm:block'>
									<Select
										value={String(pageSize)}
										onChange={(value) => setPageSize(Number(value || 25))}
										placeholder='Rows'
										options={[
											{ value: 10, label: '10 / page' },
											{ value: 25, label: '25 / page' },
											{ value: 50, label: '50 / page' },
											{ value: 100, label: '100 / page' },
										]}
									/>
								</div>

								<button
									type='button'
									onClick={() => setPage((previous) => Math.max(1, previous - 1))}
									disabled={page <= 1 || loading}
									className={classNames(
										'h-9 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 shadow-sm',
										'hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed',
									)}
								>
									Prev
								</button>

								<div className='min-w-[90px] text-center text-xs text-zinc-600'>
									Page <span className='font-medium text-zinc-900'>{page}</span> of{' '}
									<span className='font-medium text-zinc-900'>{pages || 1}</span>
								</div>

								<button
									type='button'
									onClick={() =>
										setPage((previous) =>
											pages ? Math.min(pages, previous + 1) : previous + 1,
										)
									}
									disabled={(pages && page >= pages) || loading}
									className={classNames(
										'h-9 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 shadow-sm',
										'hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed',
									)}
								>
									Next
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='mt-3 text-xs text-zinc-400 sm:hidden'>
				Tip: swipe horizontally to view all columns.
			</div>
		</div>
	);
}
